/**
 * SurveyQuestionsPreview Component
 * 
 * Displays AI-parsed survey questions in editable form for user review and modification
 * Specifically designed for the AI Interviewer feature
 */

import React, { useState, useEffect } from 'react';
import { Edit3, Save, X, AlertCircle, CheckCircle, ArrowLeft, Trash2, GripVertical, Plus } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT } from '../../config/ui-text';
import { PANEL_PROVIDERS } from '../../config/panel-providers';
import ImageHotspotEditor, { ImageHotspotConfig } from '../../components/survey/ImageHotspotEditor';
import MediaUploadGrid from '../../components/survey/MediaUploadGrid';
import MediaUploadIcon from '../../components/survey/MediaUploadIcon';
import GroupMediaUpload, { getGroupColor } from '../../components/survey/GroupMediaUpload';
import { getGroupLabel } from '../../utils/groupUtils';

interface ChoiceAction {
  value: string;
  action?: 'terminate' | 'skip_to';
  target?: string;  // question ID for skip_to
  reason?: string;  // reason for terminate
}

interface ParsedQuestion {
  id: string;
  text: string;
  type: string;
  choices?: string[];
  choice_actions?: ChoiceAction[];  // Action configuration for screener/skip logic
  scale_min?: number;
  scale_max?: number;
  max_selections?: number;
  ranking_count?: number;
  text_limit?: number;
  required?: boolean;
  followup_instructions?: string;
  allow_other?: boolean;
  enable_ai_followup?: boolean;
  randomize_choices?: boolean;
  randomization_group?: string | null;  // 'default' = include in randomization pool, null = fixed position
  // New question type fields
  matrix_rows?: string[];
  matrix_columns?: string[];
  conjoint_attributes?: { name: string; levels: string[] }[];
  media_urls?: string[];
  media_display_mode?: 'question' | 'choices';  // How to display media_urls for choice questions
  option_media?: { [optionIndex: number]: string[] };  // Media per option (for choice questions)
  description?: string;
  // Info Screen fields
  display_time_seconds?: number;  // For info_screen: minimum time before continue button is enabled
  // Image Hotspot fields
  image_url?: string;
  image_path?: string;
  bounding_boxes?: { id: string; label: string; x: number; y: number; width: number; height: number }[];
  // Between-subject group fields
  visible_groups?: number[];
  group_media?: Record<string, string[]>;
  option_group_media?: Record<string, Record<string, string[]>>;
  // Percentage allocation fields
  allocation_options?: string[];
  allocation_target?: number;
  auto_fill_last?: boolean;
  allow_zero?: boolean;
  // Conditional display field
  display_condition?: {
    source_question_id: string;
    // For choice-based: 'selected' | 'not_selected'
    // For percentage allocation: '>' | '>=' | '=' | '<' | '<='
    operator: 'selected' | 'not_selected' | '>' | '>=' | '=' | '<' | '<=';
    value: string;
    // For percentage allocation questions
    option_key?: string;    // Which allocation option to check
    threshold?: number;     // Numeric threshold (e.g., 0)
  };
}

interface RedirectConfig {
  redirect_base_url: string;
  tracking_param: { [key: string]: string };
  completion_params: { [key: string]: string } | null;
  screen_out_params: { [key: string]: string } | null;
  quota_full_params: { [key: string]: string } | null;
}

interface SurveyQuestionsPreviewProps {
  parsedQuestions: ParsedQuestion[];
  surveyName: string;
  onQuestionsUpdated: (questions: ParsedQuestion[], redirectConfig?: RedirectConfig | null, maxResponses?: number | null) => void;
  onCreateSurvey: (surveyName: string, questions: ParsedQuestion[], redirectConfig?: RedirectConfig | null, maxResponses?: number | null) => Promise<void>;
  onGoBack: () => void;
  isLoading?: boolean;
  className?: string;
  readOnly?: boolean;
  hideActionButtons?: boolean;
  initialMaxResponses?: number | null;
  initialRedirectConfig?: RedirectConfig | null;
  groupCount?: number;
}

// All supported question types
const QUESTION_TYPES = [
  { value: 'info_screen', label: 'Information Screen' },
  { value: 'single_choice', label: UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.QUESTION_TYPES.SINGLE_CHOICE },
  { value: 'multiple_choice', label: UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.QUESTION_TYPES.MULTIPLE_CHOICE },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'rating_scale', label: 'Rating Scale' },
  { value: 'likert_scale', label: 'Likert Scale' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'nps', label: 'Net Promoter Score' },
  { value: 'numeric_input', label: 'Numeric Input' },
  { value: 'matrix_rating', label: 'Matrix/Grid' },
  { value: 'max_diff', label: 'MaxDiff (Best/Worst)' },
  { value: 'conjoint_analysis', label: 'Conjoint Analysis (Choice-Based)' },
  { value: 'image_hotspot', label: 'Image Hotspot (Click Ranking)' },
  { value: 'percentage_allocation', label: 'Percentage Allocation' },
];

// Question types that support answer choices (mutually exclusive with scale-based types)
const CHOICE_BASED_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown', 'ranking'];

// Question types that support scale settings
const SCALE_BASED_TYPES = ['rating_scale', 'likert_scale', 'nps'];

// Question types that support ranking settings
const RANKING_BASED_TYPES = ['ranking'];

// Question types that support allocation settings
const ALLOCATION_BASED_TYPES = ['percentage_allocation'];

// Question types that support matrix settings
const MATRIX_BASED_TYPES = ['matrix_rating'];

// Question types that support MaxDiff settings
const MAX_DIFF_BASED_TYPES = ['max_diff'];

// Question types that support Conjoint Analysis settings
const CONJOINT_BASED_TYPES = ['conjoint_analysis'];

// Question types that support Image Hotspot settings
const IMAGE_HOTSPOT_TYPES = ['image_hotspot'];

// Question types that support media content (images/videos)
const MEDIA_SUPPORTED_TYPES = ['single_choice', 'multiple_choice', 'short_text', 'long_text', 'info_screen'];

// Question types that support conditional actions (screener/skip logic)
const CONDITIONAL_ACTION_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown'];

// Question types that can be used as source for display conditions
const DISPLAY_CONDITION_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown', 'percentage_allocation'];

const SurveyQuestionsPreview: React.FC<SurveyQuestionsPreviewProps> = ({
  parsedQuestions,
  surveyName,
  onQuestionsUpdated,
  onCreateSurvey,
  onGoBack,
  isLoading = false,
  className = '',
  readOnly = false,
  hideActionButtons = false,
  initialMaxResponses,
  initialRedirectConfig,
  groupCount = 1,
}) => {
  const [questions, setQuestions] = useState<ParsedQuestion[]>([]);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [originalQuestionBeforeEdit, setOriginalQuestionBeforeEdit] = useState<ParsedQuestion | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  // Response quota configuration
  const [maxResponses, setMaxResponses] = useState<string>('');
  const [showQuotaConfig, setShowQuotaConfig] = useState(false);

  // Panel provider redirect configuration
  const [showRedirectConfig, setShowRedirectConfig] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [redirectBaseUrl, setRedirectBaseUrl] = useState('');
  const [trackingParamName, setTrackingParamName] = useState('');
  const [completionParams, setCompletionParams] = useState<string>('');
  const [screenOutParams, setScreenOutParams] = useState<string>('');
  const [quotaFullParams, setQuotaFullParams] = useState<string>('');

  // Media panel expansion state
  const [expandedQuestionMedia, setExpandedQuestionMedia] = useState<string | null>(null);
  const [expandedOptionMedia, setExpandedOptionMedia] = useState<{ questionId: string; optionIndex: number } | null>(null);

  // Handler for provider selection - auto-fills all fields
  const handleProviderChange = (providerId: string) => {
    setSelectedProvider(providerId);

    if (providerId && PANEL_PROVIDERS[providerId]) {
      const provider = PANEL_PROVIDERS[providerId];
      setRedirectBaseUrl(provider.redirectBaseUrl);
      setTrackingParamName(provider.trackingParamName);
      setCompletionParams(provider.completionParams);
      setScreenOutParams(provider.screenOutParams);
      setQuotaFullParams(provider.quotaFullParams);
    }
  };

  useEffect(() => {
    // Map qualitative questions to long_text and set default enable_ai_followup
    const processedQuestions = parsedQuestions.map(q => ({
      ...q,
      type: q.type === 'qualitative' ? 'long_text' : q.type,
      enable_ai_followup: q.enable_ai_followup ?? false // Disabled by default - users can enable if needed
    }));
    setQuestions(processedQuestions);
    setHasChanges(false);
    
    // Auto-open image_hotspot questions in edit mode if they don't have an image configured
    const firstHotspotWithoutImage = processedQuestions.find(
      q => q.type === 'image_hotspot' && !q.image_url
    );
    if (firstHotspotWithoutImage && !readOnly) {
      setEditingQuestionId(firstHotspotWithoutImage.id);
      setOriginalQuestionBeforeEdit(firstHotspotWithoutImage);
    }
  }, [parsedQuestions, readOnly]);

  // Auto-expand sections in readonly mode
  useEffect(() => {
    if (readOnly) {
      setShowQuotaConfig(true);
      setShowRedirectConfig(true);
    }
  }, [readOnly]);

  // Initialize quota and redirect config from props
  useEffect(() => {
    if (initialMaxResponses !== undefined && initialMaxResponses !== null) {
      setMaxResponses(initialMaxResponses.toString());
    }

    if (initialRedirectConfig) {
      setRedirectBaseUrl(initialRedirectConfig.redirect_base_url || '');

      // Extract tracking param name (first key in tracking_param object)
      const trackingKeys = Object.keys(initialRedirectConfig.tracking_param || {});
      if (trackingKeys.length > 0) {
        setTrackingParamName(trackingKeys[0]);
      }

      // Convert param objects to comma-separated strings
      const formatParams = (params: { [key: string]: string } | null) => {
        if (!params) return '';
        return Object.entries(params).map(([key, value]) => `${key}=${value}`).join(', ');
      };

      setCompletionParams(formatParams(initialRedirectConfig.completion_params));
      setScreenOutParams(formatParams(initialRedirectConfig.screen_out_params));
      setQuotaFullParams(formatParams(initialRedirectConfig.quota_full_params));
    }
  }, [initialMaxResponses, initialRedirectConfig]);

  const updateQuestion = (questionId: string, updates: Partial<ParsedQuestion>) => {
    const updatedQuestions = questions.map(q =>
      q.id === questionId ? { ...q, ...updates } : q
    );
    setQuestions(updatedQuestions);
    setHasChanges(true);
    // Don't save to parent until user clicks save
  };

  const removeQuestion = (questionId: string) => {
    // Find questions that depend on this question (have display_condition referencing it)
    const dependentQuestions = questions.filter(
      q => q.display_condition?.source_question_id === questionId
    );

    // Build confirmation message
    let confirmMessage = 'Are you sure you want to delete this question? This action cannot be undone.';
    if (dependentQuestions.length > 0) {
      const questionIndex = questions.findIndex(q => q.id === questionId) + 1;
      const dependentList = dependentQuestions
        .map(q => `Q${questions.findIndex(dq => dq.id === q.id) + 1}`)
        .join(', ');
      confirmMessage = `Question Q${questionIndex} is used as a condition source for: ${dependentList}.\n\nDeleting this question will remove the display conditions from these dependent questions.\n\nAre you sure you want to continue?`;
    }

    if (window.confirm(confirmMessage)) {
      // Remove the question and clear display_condition from dependent questions
      const updatedQuestions = questions
        .filter(q => q.id !== questionId)
        .map(q => {
          // If this question depended on the deleted question, remove its condition
          if (q.display_condition?.source_question_id === questionId) {
            const { display_condition, ...rest } = q;
            return rest as ParsedQuestion;
          }
          return q;
        });

      setQuestions(updatedQuestions);
      setHasChanges(true);
      onQuestionsUpdated(updatedQuestions);

      // If we were editing this question, clear edit state
      if (editingQuestionId === questionId) {
        setEditingQuestionId(null);
        setOriginalQuestionBeforeEdit(null);
      }
    }
  };

  const addNewQuestion = () => {
    insertQuestionAt(questions.length);
  };

  // Insert a new question at a specific index
  const insertQuestionAt = (index: number) => {
    const newQuestion: ParsedQuestion = {
      id: `question_${Date.now()}`,
      text: '',
      type: 'single_choice',
      choices: ['Option 1', 'Option 2'],
      required: true,
      enable_ai_followup: false,
    };

    const updatedQuestions = [
      ...questions.slice(0, index),
      newQuestion,
      ...questions.slice(index)
    ];
    setQuestions(updatedQuestions);
    setHasChanges(true);
    onQuestionsUpdated(updatedQuestions);

    // Automatically open the new question for editing
    setOriginalQuestionBeforeEdit(newQuestion);
    setEditingQuestionId(newQuestion.id);
  };

  const addChoice = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const choices = question.choices || [];
      updateQuestion(questionId, {
        choices: [...choices, `Option ${choices.length + 1}`]
      });
    }
  };

  const removeChoice = (questionId: string, choiceIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const choices = question.choices.filter((_, index) => index !== choiceIndex);
      // Also remove the corresponding media URL if it exists
      const mediaUrls = question.media_urls
        ? question.media_urls.filter((_, index) => index !== choiceIndex)
        : undefined;
      
      // Remove the corresponding choice_action at the same index
      const choiceActions = question.choice_actions
        ? question.choice_actions.filter((_, index) => index !== choiceIndex)
        : undefined;
      
      // Re-index option_media keys (shift indices down for entries after removed index)
      let optionMedia: { [optionIndex: number]: string[] } | undefined = undefined;
      if (question.option_media) {
        optionMedia = {};
        Object.entries(question.option_media).forEach(([key, urls]) => {
          const oldIndex = parseInt(key);
          if (oldIndex < choiceIndex) {
            // Keep indices before removed choice unchanged
            optionMedia![oldIndex] = urls;
          } else if (oldIndex > choiceIndex) {
            // Shift indices after removed choice down by 1
            optionMedia![oldIndex - 1] = urls;
          }
          // Skip the removed choice index entirely
        });
        // Clean up if empty
        if (Object.keys(optionMedia).length === 0) {
          optionMedia = undefined;
        }
      }
      
      updateQuestion(questionId, {
        choices,
        media_urls: mediaUrls && mediaUrls.length > 0 ? mediaUrls : undefined,
        choice_actions: choiceActions && choiceActions.length > 0 ? choiceActions : undefined,
        option_media: optionMedia
      });
    }
  };

  const updateChoice = (questionId: string, choiceIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const choices = [...question.choices];
      choices[choiceIndex] = value;
      
      // Also update the value field in choice_actions to keep them in sync
      // (backend matches skip logic by value field)
      let choiceActions = question.choice_actions;
      if (choiceActions && choiceActions[choiceIndex]) {
        choiceActions = [...choiceActions];
        choiceActions[choiceIndex] = {
          ...choiceActions[choiceIndex],
          value: value
        };
      }
      
      updateQuestion(questionId, { 
        choices,
        ...(choiceActions ? { choice_actions: choiceActions } : {})
      });
    }
  };

  // Helper functions for question-level media management
  // Using functional state updates to handle rapid successive calls properly
  const addQuestionMedia = (questionId: string, url: string) => {
    setQuestions(prevQuestions => 
      prevQuestions.map(q => 
        q.id === questionId 
          ? { ...q, media_urls: [...(q.media_urls || []), url] }
          : q
      )
    );
    setHasChanges(true);
  };

  const removeQuestionMedia = (questionId: string, index: number) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q =>
        q.id === questionId && q.media_urls
          ? { ...q, media_urls: q.media_urls.filter((_, i) => i !== index) }
          : q
      )
    );
    setHasChanges(true);
  };

  // Helper functions for option-level media management
  const addOptionMedia = (questionId: string, optionIndex: number, url: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId) return q;
        const optionMedia = { ...(q.option_media || {}) };
        optionMedia[optionIndex] = [...(optionMedia[optionIndex] || []), url];
        return { ...q, option_media: optionMedia };
      })
    );
    setHasChanges(true);
  };

  const removeOptionMedia = (questionId: string, optionIndex: number, mediaIndex: number) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId || !q.option_media || !q.option_media[optionIndex]) return q;
        const optionMedia = { ...(q.option_media || {}) };
        optionMedia[optionIndex] = optionMedia[optionIndex].filter((_, i) => i !== mediaIndex);
        // Clean up empty arrays
        if (optionMedia[optionIndex].length === 0) {
          delete optionMedia[optionIndex];
        }
        return { ...q, option_media: optionMedia };
      })
    );
    setHasChanges(true);
  };

  const getOptionMediaUrls = (question: ParsedQuestion, optionIndex: number): string[] => {
    return question.option_media?.[String(optionIndex)] || question.option_media?.[optionIndex] || [];
  };

  // Helper functions for managing choice actions (screener/skip logic)
  const updateChoiceAction = (
    questionId: string,
    choiceIndex: number,
    action?: 'terminate' | 'skip_to',
    target?: string,
    reason?: string
  ) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.choices) return;

    // Get or create choice_actions array matching choices length
    const choiceActions: ChoiceAction[] = question.choices.map((choice, idx) => {
      const existingAction = question.choice_actions?.[idx];

      if (idx === choiceIndex) {
        // Update this choice's action
        return {
          value: choice,
          action,
          target: action === 'skip_to' ? target : undefined,
          reason: action === 'terminate' ? reason : undefined
        };
      }

      // Preserve existing action or create default
      return existingAction || { value: choice };
    });

    updateQuestion(questionId, { choice_actions: choiceActions });
  };

  // Get action metadata for a specific choice
  const getChoiceAction = (question: ParsedQuestion, choiceIndex: number): ChoiceAction | undefined => {
    return question.choice_actions?.[choiceIndex];
  };

  const addMatrixItem = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const items = question.matrix_rows || [];
      updateQuestion(questionId, {
        matrix_rows: [...items, `Item ${items.length + 1}`]
      });
    }
  };

  const removeMatrixItem = (questionId: string, itemIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.matrix_rows) {
      const items = question.matrix_rows.filter((_, index) => index !== itemIndex);
      updateQuestion(questionId, { matrix_rows: items });
    }
  };

  const updateMatrixItem = (questionId: string, itemIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.matrix_rows) {
      const items = [...question.matrix_rows];
      items[itemIndex] = value;
      updateQuestion(questionId, { matrix_rows: items });
    }
  };

  const addConjointAttribute = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const attributes = question.conjoint_attributes || [];
      updateQuestion(questionId, {
        conjoint_attributes: [...attributes, { name: `Attribute ${attributes.length + 1}`, levels: ['Level 1', 'Level 2'] }]
      });
    }
  };

  const removeConjointAttribute = (questionId: string, attrIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = question.conjoint_attributes.filter((_, index) => index !== attrIndex);
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const updateConjointAttribute = (questionId: string, attrIndex: number, field: string, value: any) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      attributes[attrIndex] = { ...attributes[attrIndex], [field]: value };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    const questionType = QUESTION_TYPES.find(qt => qt.value === type);
    return questionType ? questionType.label : type;
  };

  const hasChoices = (type: string) => {
    return CHOICE_BASED_TYPES.includes(type);
  };

  const hasScale = (type: string) => {
    return SCALE_BASED_TYPES.includes(type);
  };

  const hasRanking = (type: string) => {
    return RANKING_BASED_TYPES.includes(type);
  };

  const hasMatrix = (type: string) => {
    return MATRIX_BASED_TYPES.includes(type);
  };

  const hasMaxDiff = (type: string) => {
    return MAX_DIFF_BASED_TYPES.includes(type);
  };

  const hasConjoint = (type: string) => {
    return CONJOINT_BASED_TYPES.includes(type);
  };

  const hasImageHotspot = (type: string) => {
    return IMAGE_HOTSPOT_TYPES.includes(type);
  };

  const hasAllocation = (type: string) => {
    return ALLOCATION_BASED_TYPES.includes(type);
  };

  // Helper function to parse comma-separated key=value pairs into object
  const parseParams = (paramsString: string): { [key: string]: string } => {
    if (!paramsString.trim()) return {};

    const params: { [key: string]: string } = {};
    const pairs = paramsString.split(',').map(p => p.trim()).filter(p => p);

    for (const pair of pairs) {
      const [key, value] = pair.split('=').map(s => s.trim());
      if (key && value) {
        params[key] = value;
      }
    }

    return params;
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Don't allow dragging while editing or in readonly mode
    if (editingQuestionId || readOnly) {
      e.preventDefault();
      return;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set drag image opacity
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setDropTargetIndex(index);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setDropTargetIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDropTargetIndex(null);
      return;
    }

    // Reorder the questions array
    const reorderedQuestions = Array.from(questions);
    const [draggedQuestion] = reorderedQuestions.splice(draggedIndex, 1);
    reorderedQuestions.splice(dropIndex, 0, draggedQuestion);

    setQuestions(reorderedQuestions);
    setHasChanges(true);
    onQuestionsUpdated(reorderedQuestions);

    // Reset drag state
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleCreateSurvey = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Build redirect config if any fields are filled
      let redirectConfig: RedirectConfig | null = null;

      if (showRedirectConfig && redirectBaseUrl.trim()) {
        // Parse parameters
        const completionParamsObj = parseParams(completionParams);
        const screenOutParamsObj = parseParams(screenOutParams);
        const quotaFullParamsObj = parseParams(quotaFullParams);

        // Build tracking param
        const trackingParam: { [key: string]: string } = {};
        if (trackingParamName.trim()) {
          trackingParam[trackingParamName.trim()] = '{TRACKING_ID}';
        }

        redirectConfig = {
          redirect_base_url: redirectBaseUrl.trim(),
          tracking_param: trackingParam,
          completion_params: Object.keys(completionParamsObj).length > 0 ? completionParamsObj : null,
          screen_out_params: Object.keys(screenOutParamsObj).length > 0 ? screenOutParamsObj : null,
          quota_full_params: Object.keys(quotaFullParamsObj).length > 0 ? quotaFullParamsObj : null,
        };
      }

      // Parse maxResponses - convert to number or null
      const maxResponsesValue = maxResponses.trim() ? parseInt(maxResponses) : null;

      await onCreateSurvey(surveyName, questions, redirectConfig, maxResponsesValue);
    } catch (error) {
      console.error('Failed to create survey:', error);
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
          <span className="text-gray-600">{UI_TEXT.LOADING.PROCESSING}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
      {/* Header - always shown for navigation */}
      <div className="mb-6">
        {!readOnly && !hideActionButtons && (
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onGoBack}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              {UI_TEXT.BUTTONS.BACK}
            </Button>
            {hasChanges && (
              <div className="flex items-center text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.UNSAVED_CHANGES}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center mb-2">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">
            {readOnly ? 'Questions' : hideActionButtons ? 'Edit Survey' : UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.TITLES.REVIEW_QUESTIONS}
          </h2>
        </div>
        <p className="text-gray-600">
          {readOnly
            ? ''
            : hideActionButtons
              ? 'Edit questions, response quota, and panel provider settings for this survey.'
              : 'Review questions, configure quota, and set up panel provider.'}
        </p>
      </div>

      {/* Questions List */}
      <div className="mb-6">
        <div className="space-y-2 mb-4">
          {questions.map((question, index) => (
            <React.Fragment key={question.id}>
              {/* Insert Question Button - appears on hover between questions */}
              {!readOnly && editingQuestionId === null && (
                <div className="group relative h-4 -my-1">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex-1 h-px bg-gray-300" />
                    <button
                      onClick={() => insertQuestionAt(index)}
                      className="mx-2 flex items-center gap-1 px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm transition-all hover:shadow-md"
                    >
                      <Plus className="w-3 h-3" />
                      Insert Question
                    </button>
                    <div className="flex-1 h-px bg-gray-300" />
                  </div>
                </div>
              )}
              
              <div
                draggable={!editingQuestionId && !readOnly}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                className={`border rounded-lg p-4 transition-colors ${
                  draggedIndex === index
                    ? 'border-blue-500 bg-blue-50 opacity-50'
                    : dropTargetIndex === index
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
            {/* Question Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <span className="text-sm font-medium text-gray-500 mr-3">
                    Q{index + 1}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getQuestionTypeLabel(question.type)}
                  </span>
                  {readOnly && question.randomization_group && (
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Randomized
                    </span>
                  )}
                  {groupCount > 1 && editingQuestionId !== question.id && (
                    <div className="ml-2 flex items-center gap-1">
                      {Array.from({ length: groupCount }, (_, gi) => {
                        const active = !question.visible_groups || question.visible_groups.includes(gi);
                        const color = getGroupColor(gi);
                        return (
                          <span
                            key={gi}
                            title={`${getGroupLabel(gi)}${active ? '' : ' (hidden)'}`}
                            className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold ${
                              active
                                ? `${color.header} text-white`
                                : 'bg-gray-200 text-gray-400'
                            }`}
                          >
                            {String.fromCharCode(65 + gi)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {editingQuestionId === question.id ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <textarea
                        value={question.text}
                        onChange={(e) => updateQuestion(question.id, { text: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md resize-none"
                        rows={2}
                        placeholder={UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.QUESTION_TEXT_PLACEHOLDER}
                      />
                      {/* Question-level Media Upload Icon */}
                      {MEDIA_SUPPORTED_TYPES.includes(question.type) && (() => {
                        const groupMediaCount = question.group_media ? Object.values(question.group_media).reduce((s, arr) => s + arr.length, 0) : 0;
                        const totalMedia = (question.media_urls?.length || 0) + groupMediaCount;
                        return (
                          <MediaUploadIcon
                            hasMedia={totalMedia > 0}
                            mediaCount={totalMedia}
                            onClick={() => setExpandedQuestionMedia(
                              expandedQuestionMedia === question.id ? null : question.id
                            )}
                          />
                        );
                      })()}
                    </div>

                    {/* Question-level Media Grid - show if has media OR is expanded */}
                    {MEDIA_SUPPORTED_TYPES.includes(question.type) && 
                     ((question.media_urls?.length || 0) > 0 || (question.group_media && Object.keys(question.group_media).length > 0) || expandedQuestionMedia === question.id) && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-xs font-medium text-gray-600 mb-2">Question Media</div>
                        {groupCount > 1 ? (
                          <GroupMediaUpload
                            groupCount={groupCount}
                            visibleGroups={question.visible_groups}
                            groupMedia={question.group_media || {}}
                            onMediaAdd={(gi, url) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                gm[String(gi)] = [...(gm[String(gi)] || []), url];
                                return { ...q, group_media: gm };
                              }));
                              setHasChanges(true);
                            }}
                            onMediaRemove={(gi, removeIdx) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                const arr = (gm[String(gi)] || []).filter((_, i) => i !== removeIdx);
                                if (arr.length > 0) gm[String(gi)] = arr;
                                else delete gm[String(gi)];
                                return { ...q, group_media: Object.keys(gm).length > 0 ? gm : undefined };
                              }));
                              setHasChanges(true);
                            }}
                            maxItemsPerGroup={5}
                          />
                        ) : (
                          <MediaUploadGrid
                            mediaUrls={question.media_urls || []}
                            onMediaAdd={(url) => addQuestionMedia(question.id, url)}
                            onMediaRemove={(index) => removeQuestionMedia(question.id, index)}
                            maxItems={5}
                          />
                        )}
                      </div>
                    )}

                    <select
                      value={question.type}
                      onChange={(e) => updateQuestion(question.id, { type: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {QUESTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>

                    {/* Visible to Groups — compact toggle row */}
                    {groupCount > 1 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-gray-500 mr-1">Groups:</span>
                        {Array.from({ length: groupCount }, (_, gi) => {
                          const selected = !question.visible_groups || question.visible_groups.includes(gi);
                          const color = getGroupColor(gi);
                          return (
                            <button
                              key={gi}
                              type="button"
                              onClick={() => {
                                const current = question.visible_groups || Array.from({ length: groupCount }, (_, i) => i);
                                const next = selected
                                  ? current.filter(g => g !== gi)
                                  : [...current, gi].sort();
                                updateQuestion(question.id, {
                                  visible_groups: next.length === groupCount ? undefined : next,
                                });
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                selected
                                  ? `${color.header} text-white shadow-sm`
                                  : 'bg-gray-100 text-gray-400 line-through'
                              }`}
                            >
                              {getGroupLabel(gi)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <p className="text-gray-900 font-medium flex-1">{question.text}</p>
                      {/* Show media count badge in read mode */}
                      {MEDIA_SUPPORTED_TYPES.includes(question.type) && ((question.media_urls?.length || 0) > 0 || (question.group_media && Object.keys(question.group_media).length > 0)) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          {question.group_media
                            ? `${Object.values(question.group_media).reduce((s, arr) => s + arr.length, 0)} media`
                            : `${question.media_urls?.length} media`
                          }
                        </span>
                      )}
                    </div>
                    {/* Show media previews in read mode */}
                    {MEDIA_SUPPORTED_TYPES.includes(question.type) && (question.media_urls?.length || 0) > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {question.media_urls?.map((url, idx) => {
                          const fullUrl = url.startsWith('/api/') 
                            ? `${import.meta.env.VITE_API_URL || ''}${url}` 
                            : url;
                          return (
                            <div key={idx} className="w-20 h-20 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                              <img
                                src={fullUrl}
                                alt={`Question media ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Show group media previews in read mode */}
                    {groupCount > 1 && question.group_media && Object.keys(question.group_media).length > 0 && (
                      <div className="space-y-2">
                        {(question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i)).map((gi) => {
                          const urls = question.group_media?.[String(gi)];
                          if (!urls || urls.length === 0) return null;
                          const color = getGroupColor(gi);
                          return (
                            <div key={gi} className={`rounded-lg border ${color.border} ${color.bg} p-2`}>
                              <span className={`text-[10px] font-semibold uppercase ${color.text}`}>{getGroupLabel(gi)}</span>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {urls.map((url, idx) => {
                                  const fullUrl = url.startsWith('/api/') ? `${import.meta.env.VITE_API_URL || ''}${url}` : url;
                                  return (
                                    <div key={idx} className="w-16 h-16 rounded border border-gray-200 overflow-hidden bg-white">
                                      <img src={fullUrl} alt={`${getGroupLabel(gi)} media ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-3 ml-4">
                {/* Question Randomization Toggle - right side */}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateQuestion(question.id, { 
                        randomization_group: question.randomization_group === 'default' ? null : 'default' 
                      });
                    }}
                    className="group relative flex items-center"
                    title="Add to Randomization Pool"
                  >
                    <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                      question.randomization_group === 'default' 
                        ? 'bg-blue-600' 
                        : 'bg-gray-300'
                    }`}>
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        question.randomization_group === 'default' 
                          ? 'translate-x-4' 
                          : 'translate-x-0.5'
                      }`} />
                    </div>
                    {/* Tooltip */}
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      Add to Randomization Pool
                    </span>
                  </button>
                )}
                {/* Drag Handle - positioned on the right */}
                {!readOnly && !editingQuestionId && (
                  <button
                    type="button"
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical className="w-5 h-5" />
                  </button>
                )}
                {editingQuestionId === question.id ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Build redirect config
                        let redirectConfig: RedirectConfig | null = null;
                        if (redirectBaseUrl.trim()) {
                          const completionParamsObj = parseParams(completionParams);
                          const screenOutParamsObj = parseParams(screenOutParams);
                          const quotaFullParamsObj = parseParams(quotaFullParams);

                          const trackingParam: { [key: string]: string } = {};
                          if (trackingParamName.trim()) {
                            trackingParam[trackingParamName.trim()] = '{TRACKING_ID}';
                          }

                          redirectConfig = {
                            redirect_base_url: redirectBaseUrl.trim(),
                            tracking_param: trackingParam,
                            completion_params: Object.keys(completionParamsObj).length > 0 ? completionParamsObj : null,
                            screen_out_params: Object.keys(screenOutParamsObj).length > 0 ? screenOutParamsObj : null,
                            quota_full_params: Object.keys(quotaFullParamsObj).length > 0 ? quotaFullParamsObj : null,
                          };
                        }

                        // Parse maxResponses
                        const maxResponsesValue = maxResponses.trim() ? parseInt(maxResponses) : null;

                        // Save changes to parent with quota and redirect config
                        onQuestionsUpdated(questions, redirectConfig, maxResponsesValue);
                        setEditingQuestionId(null);
                        setOriginalQuestionBeforeEdit(null);
                        setHasChanges(false);
                      }}
                      icon={<Save className="w-4 h-4" />}
                    >
                      {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.SAVE}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Restore original question if we have it
                        if (originalQuestionBeforeEdit) {
                          const restoredQuestions = questions.map(q =>
                            q.id === originalQuestionBeforeEdit.id ? originalQuestionBeforeEdit : q
                          );
                          setQuestions(restoredQuestions);
                        }
                        setEditingQuestionId(null);
                        setOriginalQuestionBeforeEdit(null);
                        setHasChanges(false);
                      }}
                      icon={<X className="w-4 h-4" />}
                    >
                      {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CANCEL}
                    </Button>
                  </>
                ) : (
                  !readOnly && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }}
                        icon={<Edit3 className="w-4 h-4" />}
                      >
                        {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.EDIT}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(question.id)}
                        icon={<Trash2 className="w-4 h-4 text-red-600" />}
                        className="text-red-600 hover:text-red-700"
                      >
                        Delete
                      </Button>
                    </>
                  )
                )}
              </div>
            </div>

            {/* Question Details */}
            <div className="mt-3 space-y-3">
              {/* Choices for choice-based questions */}
              {hasChoices(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.ANSWER_CHOICES}
                  </label>
                  <div className="space-y-3">
                    {(question.choices || []).map((choice, choiceIndex) => {
                      const supportsMedia = MEDIA_SUPPORTED_TYPES.includes(question.type);
                      const hasMediaContent = (question.media_urls && question.media_urls.length > 0)
                        || (question.option_media && Object.keys(question.option_media).length > 0)
                        || (question.group_media && Object.keys(question.group_media).length > 0)
                        || (question.option_group_media && Object.keys(question.option_group_media).length > 0);
                      const showMediaLayout = supportsMedia && (hasMediaContent || editingQuestionId === question.id);

                      return (
                        <div key={choiceIndex} className={`${showMediaLayout ? 'border border-gray-200 rounded-lg p-3' : 'flex items-center space-x-2'}`}>
                          {showMediaLayout ? (
                            // Media + Choice Layout
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-1">
                                  <span className="text-sm text-gray-500 w-8">
                                    {choiceIndex + 1}.
                                  </span>
                                  {editingQuestionId === question.id ? (
                                    <>
                                      <input
                                        type="text"
                                        value={choice}
                                        onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                        className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                      />
                                      {/* Option-level Media Upload Icon */}
                                      <MediaUploadIcon
                                        hasMedia={getOptionMediaUrls(question, choiceIndex).length > 0}
                                        mediaCount={getOptionMediaUrls(question, choiceIndex).length}
                                        onClick={() => setExpandedOptionMedia(
                                          expandedOptionMedia?.questionId === question.id && expandedOptionMedia?.optionIndex === choiceIndex
                                            ? null
                                            : { questionId: question.id, optionIndex: choiceIndex }
                                        )}
                                      />
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeChoice(question.id, choiceIndex)}
                                        icon={<X className="w-3 h-3" />}
                                      >
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{choice}</span>
                                      </div>
                                      {/* Option media preview in read mode */}
                                      {getOptionMediaUrls(question, choiceIndex).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {getOptionMediaUrls(question, choiceIndex).map((url, mediaIdx) => {
                                            const fullUrl = url.startsWith('/api/') 
                                              ? `${import.meta.env.VITE_API_URL || ''}${url}` 
                                              : url;
                                            return (
                                              <div key={mediaIdx} className="w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                                                <img
                                                  src={fullUrl}
                                                  alt={`${choice} media ${mediaIdx + 1}`}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                  }}
                                                />
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      {/* Option group media preview in read mode */}
                                      {groupCount > 1 && question.option_group_media?.[String(choiceIndex)] && Object.keys(question.option_group_media[String(choiceIndex)]).length > 0 && (
                                        <div className="mt-2 space-y-1.5">
                                          {(question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i)).map((gi) => {
                                            const urls = question.option_group_media?.[String(choiceIndex)]?.[String(gi)];
                                            if (!urls || urls.length === 0) return null;
                                            const color = getGroupColor(gi);
                                            return (
                                              <div key={gi} className={`rounded border ${color.border} ${color.bg} px-2 py-1 flex items-center gap-2`}>
                                                <span className={`text-[9px] font-semibold uppercase ${color.text} shrink-0`}>{getGroupLabel(gi)}</span>
                                                <div className="flex flex-wrap gap-1">
                                                  {urls.map((url, idx) => {
                                                    const fullUrl = url.startsWith('/api/') ? `${import.meta.env.VITE_API_URL || ''}${url}` : url;
                                                    return (
                                                      <div key={idx} className="w-10 h-10 rounded border border-gray-200 overflow-hidden bg-white">
                                                        <img src={fullUrl} alt={`${getGroupLabel(gi)} ${choice} ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Option-level Media Grid - show if has images OR is explicitly expanded */}
                              {editingQuestionId === question.id && 
                               (getOptionMediaUrls(question, choiceIndex).length > 0 ||
                                (expandedOptionMedia?.questionId === question.id && 
                                 expandedOptionMedia?.optionIndex === choiceIndex)) && (
                                <div className="ml-10 p-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="text-xs font-medium text-gray-600 mb-2">Option Media</div>
                                  {groupCount > 1 ? (
                                    <GroupMediaUpload
                                      groupCount={groupCount}
                                      visibleGroups={question.visible_groups}
                                      groupMedia={question.option_group_media?.[String(choiceIndex)] || {}}
                                      onMediaAdd={(gi, url) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          perOpt[String(gi)] = [...(perOpt[String(gi)] || []), url];
                                          ogm[String(choiceIndex)] = perOpt;
                                          return { ...q, option_group_media: ogm };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      onMediaRemove={(gi, removeIdx) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          const arr = (perOpt[String(gi)] || []).filter((_, i) => i !== removeIdx);
                                          if (arr.length > 0) perOpt[String(gi)] = arr;
                                          else delete perOpt[String(gi)];
                                          if (Object.keys(perOpt).length > 0) ogm[String(choiceIndex)] = perOpt;
                                          else delete ogm[String(choiceIndex)];
                                          return { ...q, option_group_media: Object.keys(ogm).length > 0 ? ogm : undefined };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      maxItemsPerGroup={3}
                                      compact
                                    />
                                  ) : (
                                    <MediaUploadGrid
                                      mediaUrls={getOptionMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                      onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                      maxItems={3}
                                      compact
                                    />
                                  )}
                                </div>
                              )}

                              {/* Action Display in Read Mode - only show if action exists and not editing */}
                              {(readOnly || editingQuestionId !== question.id) && CONDITIONAL_ACTION_TYPES.includes(question.type) && getChoiceAction(question, choiceIndex)?.action && (
                                <div className="ml-10 mt-1 flex items-center space-x-2">
                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      🚫 Terminates survey
                                      {getChoiceAction(question, choiceIndex)?.reason && ` (${getChoiceAction(question, choiceIndex)?.reason})`}
                                    </span>
                                  )}
                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      ➜ Skips to: {questions.find(q => q.id === getChoiceAction(question, choiceIndex)?.target)?.text.substring(0, 40) || 'Unknown question'}...
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Action Configuration for Media Layout - only show in edit mode for conditional action types */}
                              {editingQuestionId === question.id && CONDITIONAL_ACTION_TYPES.includes(question.type) && (
                                <div className="ml-10 mt-2 flex items-center space-x-2">
                                  <label className="text-xs text-gray-600 min-w-fit">Action:</label>
                                  <select
                                    value={getChoiceAction(question, choiceIndex)?.action || 'none'}
                                    onChange={(e) => {
                                      const action = e.target.value === 'none' ? undefined : e.target.value as 'terminate' | 'skip_to';
                                      updateChoiceAction(question.id, choiceIndex, action);
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                                  >
                                    <option value="none">Continue normally</option>
                                    <option value="terminate">Terminate (disqualify)</option>
                                    <option value="skip_to">Skip to question</option>
                                  </select>

                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <select
                                      value={getChoiceAction(question, choiceIndex)?.target || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'skip_to', e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="">Select question...</option>
                                      {questions.map(q => (
                                        <option key={q.id} value={q.id}>{q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}</option>
                                      ))}
                                    </select>
                                  )}

                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <input
                                      type="text"
                                      value={getChoiceAction(question, choiceIndex)?.reason || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'terminate', undefined, e.target.value)}
                                      placeholder="Reason (optional)"
                                      className="px-2 py-1 border border-gray-300 rounded text-xs flex-1"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            // Regular Choice Layout
                            <div className="flex flex-col space-y-2 w-full">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500 w-8">
                                  {choiceIndex + 1}.
                                </span>
                                {editingQuestionId === question.id ? (
                                  <>
                                    <input
                                      type="text"
                                      value={choice}
                                      onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                      className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                    />
                                    {/* Option-level Media Upload Icon for regular layout */}
                                    {supportsMedia && (
                                      <MediaUploadIcon
                                        hasMedia={getOptionMediaUrls(question, choiceIndex).length > 0}
                                        mediaCount={getOptionMediaUrls(question, choiceIndex).length}
                                        onClick={() => setExpandedOptionMedia(
                                          expandedOptionMedia?.questionId === question.id && expandedOptionMedia?.optionIndex === choiceIndex
                                            ? null
                                            : { questionId: question.id, optionIndex: choiceIndex }
                                        )}
                                      />
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeChoice(question.id, choiceIndex)}
                                      icon={<X className="w-3 h-3" />}
                                    >
                                    </Button>
                                  </>
                                ) : (
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-900">{choice}</span>
                                    </div>
                                    {/* Option media preview in read mode */}
                                    {getOptionMediaUrls(question, choiceIndex).length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {getOptionMediaUrls(question, choiceIndex).map((url, mediaIdx) => {
                                          const fullUrl = url.startsWith('/api/') 
                                            ? `${import.meta.env.VITE_API_URL || ''}${url}` 
                                            : url;
                                          return (
                                            <div key={mediaIdx} className="w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                                              <img
                                                src={fullUrl}
                                                alt={`${choice} media ${mediaIdx + 1}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                  const target = e.target as HTMLImageElement;
                                                  target.style.display = 'none';
                                                }}
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Option-level Media Grid for regular layout - show if has images OR is explicitly expanded */}
                              {editingQuestionId === question.id && 
                               supportsMedia &&
                               (getOptionMediaUrls(question, choiceIndex).length > 0 ||
                                (expandedOptionMedia?.questionId === question.id && 
                                 expandedOptionMedia?.optionIndex === choiceIndex)) && (
                                <div className="ml-10 p-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="text-xs font-medium text-gray-600 mb-2">Option Media</div>
                                  {groupCount > 1 ? (
                                    <GroupMediaUpload
                                      groupCount={groupCount}
                                      visibleGroups={question.visible_groups}
                                      groupMedia={question.option_group_media?.[String(choiceIndex)] || {}}
                                      onMediaAdd={(gi, url) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          perOpt[String(gi)] = [...(perOpt[String(gi)] || []), url];
                                          ogm[String(choiceIndex)] = perOpt;
                                          return { ...q, option_group_media: ogm };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      onMediaRemove={(gi, removeIdx) => {
                                        setQuestions(prev => prev.map(q => {
                                          if (q.id !== question.id) return q;
                                          const ogm = { ...(q.option_group_media || {}) };
                                          const perOpt = { ...(ogm[String(choiceIndex)] || {}) };
                                          const arr = (perOpt[String(gi)] || []).filter((_, i) => i !== removeIdx);
                                          if (arr.length > 0) perOpt[String(gi)] = arr;
                                          else delete perOpt[String(gi)];
                                          if (Object.keys(perOpt).length > 0) ogm[String(choiceIndex)] = perOpt;
                                          else delete ogm[String(choiceIndex)];
                                          return { ...q, option_group_media: Object.keys(ogm).length > 0 ? ogm : undefined };
                                        }));
                                        setHasChanges(true);
                                      }}
                                      maxItemsPerGroup={3}
                                      compact
                                    />
                                  ) : (
                                    <MediaUploadGrid
                                      mediaUrls={getOptionMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                      onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                      maxItems={3}
                                      compact
                                    />
                                  )}
                                </div>
                              )}

                              {/* Action Display in Read Mode - only show if action exists and not editing */}
                              {(readOnly || editingQuestionId !== question.id) && CONDITIONAL_ACTION_TYPES.includes(question.type) && getChoiceAction(question, choiceIndex)?.action && (
                                <div className="ml-10 mt-1 flex items-center space-x-2">
                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      🚫 Terminates survey
                                      {getChoiceAction(question, choiceIndex)?.reason && ` (${getChoiceAction(question, choiceIndex)?.reason})`}
                                    </span>
                                  )}
                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      ➜ Skips to: {questions.find(q => q.id === getChoiceAction(question, choiceIndex)?.target)?.text.substring(0, 40) || 'Unknown question'}...
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Action Configuration for Regular Layout - only show in edit mode for conditional action types */}
                              {editingQuestionId === question.id && CONDITIONAL_ACTION_TYPES.includes(question.type) && (
                                <div className="ml-10 flex items-center space-x-2">
                                  <label className="text-xs text-gray-600 min-w-fit">Action:</label>
                                  <select
                                    value={getChoiceAction(question, choiceIndex)?.action || 'none'}
                                    onChange={(e) => {
                                      const action = e.target.value === 'none' ? undefined : e.target.value as 'terminate' | 'skip_to';
                                      updateChoiceAction(question.id, choiceIndex, action);
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                                  >
                                    <option value="none">Continue normally</option>
                                    <option value="terminate">Terminate (disqualify)</option>
                                    <option value="skip_to">Skip to question</option>
                                  </select>

                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <select
                                      value={getChoiceAction(question, choiceIndex)?.target || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'skip_to', e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="">Select question...</option>
                                      {questions.map(q => (
                                        <option key={q.id} value={q.id}>{q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}</option>
                                      ))}
                                    </select>
                                  )}

                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <input
                                      type="text"
                                      value={getChoiceAction(question, choiceIndex)?.reason || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'terminate', undefined, e.target.value)}
                                      placeholder="Reason (optional)"
                                      className="px-2 py-1 border border-gray-300 rounded text-xs flex-1"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Show "Other" indicator when allow_other is enabled - only for single_choice and multiple_choice WITHOUT media */}
                    {question.allow_other && editingQuestionId !== question.id &&
                     (question.type === 'single_choice' || question.type === 'multiple_choice') &&
                     (!question.media_urls || question.media_urls.length === 0) && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-sm text-gray-500 w-8">
                          +
                        </span>
                        <span className="text-sm text-gray-600">
                          {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.OTHER_OPTION_INDICATOR}
                        </span>
                      </div>
                    )}

                    {/* Show "Randomized" indicator when randomize_choices is enabled - for single_choice and multiple_choice */}
                    {question.randomize_choices && editingQuestionId !== question.id &&
                     (question.type === 'single_choice' || question.type === 'multiple_choice') && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-sm text-gray-500 w-8">
                          ~
                        </span>
                        <span className="text-sm text-gray-600">
                          Choices will be randomized
                        </span>
                      </div>
                    )}
                    {editingQuestionId === question.id && (
                      <div className="space-y-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addChoice(question.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.ADD_CHOICE}
                        </Button>

                        {/* Allow Other Option Toggle - only for single_choice and multiple_choice WITHOUT media */}
                        {(question.type === 'single_choice' || question.type === 'multiple_choice') &&
                         (!question.media_urls || question.media_urls.length === 0) && (
                          <div className="flex items-center space-x-2 pt-2">
                            <input
                              type="checkbox"
                              id={`allow-other-${question.id}`}
                              checked={question.allow_other || false}
                              onChange={(e) => updateQuestion(question.id, { allow_other: e.target.checked })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor={`allow-other-${question.id}`}
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.ALLOW_OTHER_OPTION}
                            </label>
                          </div>
                        )}

                        {/* Randomize Responses Toggle - only for single_choice and multiple_choice */}
                        {(question.type === 'single_choice' || question.type === 'multiple_choice') && (
                          <div className="flex items-center space-x-2 pt-2">
                            <input
                              type="checkbox"
                              id={`randomize-choices-${question.id}`}
                              checked={question.randomize_choices || false}
                              onChange={(e) => updateQuestion(question.id, { randomize_choices: e.target.checked })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor={`randomize-choices-${question.id}`}
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              Randomize Responses
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Scale settings for scale-based questions */}
              {hasScale(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scale Settings
                  </label>
                  {editingQuestionId === question.id ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Min Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_min || (question.type === 'nps' ? 0 : 1)}
                          onChange={(e) => updateQuestion(question.id, { scale_min: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                          min={question.type === 'nps' ? 0 : 1}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Max Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_max || (question.type === 'nps' ? 10 : 5)}
                          onChange={(e) => updateQuestion(question.id, { scale_max: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                          min={question.type === 'nps' ? 0 : 1}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4 text-sm text-gray-700">
                      <span>
                        <span className="font-medium">Min:</span> {question.scale_min || (question.type === 'nps' ? 0 : 1)}
                      </span>
                      <span>
                        <span className="font-medium">Max:</span> {question.scale_max || (question.type === 'nps' ? 10 : 5)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Ranking settings for ranking questions */}
              {hasRanking(question.type) && editingQuestionId === question.id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ranking Settings
                  </label>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Number of items to rank
                    </label>
                    <input
                      type="number"
                      value={question.ranking_count || 3}
                      onChange={(e) => updateQuestion(question.id, { ranking_count: parseInt(e.target.value) })}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                      min={1}
                      max={question.choices?.length || 5}
                    />
                  </div>
                </div>
              )}

              {/* Matrix settings for matrix_rating questions */}
              {hasMatrix(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Matrix/Grid Settings
                  </label>

                  {/* Matrix Items */}
                  <div className="mb-4">
                    <label className="block text-xs text-gray-600 mb-1">
                      Items to Rate
                    </label>
                    <div className="space-y-2">
                      {(question.matrix_rows || []).map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {itemIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => updateMatrixItem(question.id, itemIndex, e.target.value)}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Item to rate"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeMatrixItem(question.id, itemIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{item}</span>
                          )}
                        </div>
                      ))}

                      {editingQuestionId === question.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addMatrixItem(question.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Add Matrix Item
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Scale Labels */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Scale Labels
                    </label>
                    {editingQuestionId === question.id ? (
                      <input
                        type="text"
                        value={question.matrix_columns?.join(', ') || '1, 2, 3, 4, 5'}
                        onChange={(e) => updateQuestion(question.id, {
                          matrix_columns: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        placeholder="1, 2, 3, 4, 5"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">
                        {question.matrix_columns?.join(', ') || '1, 2, 3, 4, 5'}
                      </div>
                    )}
                  </div>

                  {/* Randomize Items Toggle - only in edit mode */}
                  {editingQuestionId === question.id && (
                    <div className="flex items-center space-x-2 pt-2">
                      <input
                        type="checkbox"
                        id={`randomize-matrix-${question.id}`}
                        checked={question.randomize_choices || false}
                        onChange={(e) => updateQuestion(question.id, { randomize_choices: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`randomize-matrix-${question.id}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        Randomize Items (scale labels remain fixed)
                      </label>
                    </div>
                  )}

                  {/* Show "Randomized" indicator when randomize_choices is enabled */}
                  {question.randomize_choices && editingQuestionId !== question.id && (
                    <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-500">
                        ~
                      </span>
                      <span className="text-sm text-gray-600">
                        Items will be randomized (scale labels remain fixed)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Percentage Allocation settings */}
              {hasAllocation(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Percentage Allocation Settings
                  </label>

                  {editingQuestionId === question.id && (
                    <p className="text-xs text-gray-500 mb-2">
                      Respondents will allocate percentages across the options. The total must equal the target (default: 100%).
                    </p>
                  )}

                  {/* Allocation Options */}
                  <div className="mb-4">
                    <label className="block text-xs text-gray-600 mb-1">
                      Options to Allocate
                    </label>
                    <div className="space-y-2">
                      {(question.allocation_options || question.choices || []).map((option: string, optionIndex: number) => (
                        <div key={optionIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {optionIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => {
                                  const options = [...(question.allocation_options || question.choices || [])];
                                  options[optionIndex] = e.target.value;
                                  updateQuestion(question.id, { allocation_options: options, choices: options });
                                }}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Option name"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const options = (question.allocation_options || question.choices || []).filter((_: string, i: number) => i !== optionIndex);
                                  updateQuestion(question.id, { allocation_options: options, choices: options });
                                }}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{option}</span>
                          )}
                        </div>
                      ))}

                      {editingQuestionId === question.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const options = [...(question.allocation_options || question.choices || []), `Option ${(question.allocation_options || question.choices || []).length + 1}`];
                            updateQuestion(question.id, { allocation_options: options, choices: options });
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Add Option
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Allocation Target */}
                  {editingQuestionId === question.id && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-600 mb-1">
                        Target Total
                      </label>
                      <input
                        type="number"
                        value={question.allocation_target || 100}
                        onChange={(e) => updateQuestion(question.id, { allocation_target: parseInt(e.target.value) || 100 })}
                        className="w-24 px-3 py-1 border border-gray-300 rounded text-sm"
                        min={1}
                        max={1000}
                      />
                    </div>
                  )}

                  {/* Auto-fill Last & Allow Zero toggles */}
                  {editingQuestionId === question.id && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`auto-fill-${question.id}`}
                          checked={question.auto_fill_last !== false}
                          onChange={(e) => updateQuestion(question.id, { auto_fill_last: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`auto-fill-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Auto-calculate last option
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`allow-zero-${question.id}`}
                          checked={question.allow_zero !== false}
                          onChange={(e) => updateQuestion(question.id, { allow_zero: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`allow-zero-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Allow 0 for individual options
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Show settings summary when not editing */}
                  {editingQuestionId !== question.id && (
                    <div className="text-sm text-gray-600 mt-2">
                      Target: {question.allocation_target || 100}
                      {question.auto_fill_last !== false && ' • Auto-fill last option'}
                      {question.allow_zero === false && ' • No zeros allowed'}
                    </div>
                  )}
                </div>
              )}

              {/* MaxDiff settings for max_diff questions */}
              {hasMaxDiff(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MaxDiff Settings
                  </label>

                  {editingQuestionId === question.id && (
                    <p className="text-xs text-gray-500 mb-2">
                      MaxDiff questions present respondents with sets of options where they select the most and least appealing choices. Edit the claims/options below.
                    </p>
                  )}

                  {/* MaxDiff Claims/Options */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Claims/Options
                    </label>
                    <div className="space-y-2">
                      {(question.choices || []).map((choice, choiceIndex) => (
                        <div key={choiceIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {choiceIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={choice}
                                onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Enter claim/option"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeChoice(question.id, choiceIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{choice}</span>
                          )}
                        </div>
                      ))}

                      {editingQuestionId === question.id && (
                        <div className="space-y-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addChoice(question.id)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            Add Claim/Option
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Information about BIBD generation */}
                    {question.choices && question.choices.length > 0 && editingQuestionId !== question.id && (
                      <div className="mt-2 text-xs text-blue-600">
                        ℹ️ {question.choices.length} claims will be shown in balanced sets of 4 using MaxDiff algorithm (balanced item frequency & pair co-occurrence for statistical validity)
                      </div>
                    )}

                    {(!question.choices || question.choices.length === 0) && editingQuestionId !== question.id && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <div className="text-xs text-yellow-800">
                          ⚠️ No claims/options found. MaxDiff requires multiple options to compare.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Conjoint Analysis settings for conjoint_analysis questions */}
              {hasConjoint(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conjoint Analysis Settings
                  </label>

                  {/* Conjoint Description */}
                  {editingQuestionId === question.id && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-600 mb-1">
                        Question Description
                      </label>
                      <textarea
                        value={question.description || ''}
                        onChange={(e) => updateQuestion(question.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none"
                        rows={2}
                        placeholder="Additional context or instructions for this question"
                      />
                    </div>
                  )}

                  {/* Conjoint Attributes */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Attributes & Levels
                    </label>
                    <div className="space-y-3">
                      {(question.conjoint_attributes || []).map((attr, attrIndex) => (
                        <div key={attrIndex} className="border border-gray-200 rounded p-3 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              Attribute {attrIndex + 1}
                            </span>
                            {editingQuestionId === question.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeConjointAttribute(question.id, attrIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            )}
                          </div>

                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={attr.name}
                                onChange={(e) => updateConjointAttribute(question.id, attrIndex, 'name', e.target.value)}
                                className="w-full px-3 py-1 border border-gray-300 rounded text-sm mb-2"
                                placeholder="Attribute name (e.g., 'Price', 'Brand')"
                              />
                              <textarea
                                value={attr.levels.join(', ')}
                                onChange={(e) => updateConjointAttribute(question.id, attrIndex, 'levels',
                                  e.target.value.split(',').map(s => s.trim()).filter(s => s)
                                )}
                                className="w-full px-3 py-1 border border-gray-300 rounded text-sm resize-none"
                                rows={2}
                                placeholder="Levels (comma-separated, e.g., '$99, $199, $299')"
                              />
                            </>
                          ) : (
                            <>
                              <div className="text-sm font-medium text-gray-900 mb-1">{attr.name}</div>
                              <div className="text-sm text-gray-600">
                                {attr.levels.join(', ')}
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {editingQuestionId === question.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addConjointAttribute(question.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Add Attribute
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Image Hotspot settings for image_hotspot questions */}
              {hasImageHotspot(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Hotspot Settings
                  </label>
                  
                  {/* Always show editor for image_hotspot if no image, otherwise check edit mode */}
                  {(editingQuestionId === question.id || (!question.image_url && !readOnly)) ? (
                    <ImageHotspotEditor
                      config={{
                        image_url: question.image_url || '',
                        image_path: question.image_path || '',
                        ranking_count: question.ranking_count || 3,
                        bounding_boxes: question.bounding_boxes || []
                      }}
                      onChange={(config: ImageHotspotConfig) => {
                        // FIRST: If uploading image to a question not in edit mode, enter edit mode
                        // This must happen before updateQuestion to prevent editor from closing
                        if (editingQuestionId !== question.id) {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }
                        // THEN: Update the question data
                        updateQuestion(question.id, {
                          image_url: config.image_url,
                          image_path: config.image_path,
                          ranking_count: config.ranking_count,
                          bounding_boxes: config.bounding_boxes
                        });
                      }}
                      disabled={false}
                    />
                  ) : (
                    <div className="space-y-3">
                      {question.image_url ? (() => {
                        // Build full image URL (same logic as ImageHotspotEditor)
                        const API_URL = import.meta.env.VITE_API_URL || '';
                        const fullImageUrl = question.image_url.startsWith('http') 
                          ? question.image_url 
                          : `${API_URL}${question.image_url}`;
                        return (
                          <>
                            {/* Image Preview with Bounding Boxes */}
                            <div className="relative inline-block">
                              <img
                                src={fullImageUrl}
                                alt="Hotspot Preview"
                                className="max-w-md h-auto rounded-lg border border-gray-200"
                                onLoad={(e) => {
                                  const img = e.currentTarget;
                                  img.dataset.width = img.clientWidth.toString();
                                  img.dataset.height = img.clientHeight.toString();
                                }}
                              />
                              {/* Render bounding boxes on image */}
                              {question.bounding_boxes?.map((box, idx) => (
                                <div
                                  key={box.id || idx}
                                  className="absolute border-2 border-teal-500 bg-teal-500/20 pointer-events-none"
                                  style={{
                                    left: `${box.x * 100}%`,
                                    top: `${box.y * 100}%`,
                                    width: `${box.width * 100}%`,
                                    height: `${box.height * 100}%`
                                  }}
                                >
                                  <div className="absolute -top-5 left-0 bg-teal-500 text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
                                    {box.label}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Summary info */}
                            <div className="text-sm text-gray-600">
                              <span className="text-green-600">✓</span> {question.bounding_boxes?.length || 0} product region(s) defined
                              <span className="mx-2">•</span>
                              Respondents select top <span className="font-medium">{question.ranking_count || 3}</span> choice(s)
                            </div>
                          </>
                        );
                      })() : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <div className="text-sm text-yellow-800">
                            ⚠️ No image uploaded. Click Edit to add an image and define product regions.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Info Screen settings for info_screen questions */}
              {question.type === 'info_screen' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Info Screen Settings
                  </label>

                  {editingQuestionId === question.id ? (
                    <div className="space-y-3">
                      {/* Display Time Setting */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Minimum Display Time (seconds)
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="0"
                            max="300"
                            value={question.display_time_seconds || ''}
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : undefined;
                              updateQuestion(question.id, { display_time_seconds: value });
                            }}
                            className="w-24 px-3 py-2 border border-gray-300 rounded text-sm"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-500">seconds</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          If set, the Continue button will be disabled until the timer completes. Leave empty for no timer.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm text-gray-600">
                      {question.display_time_seconds ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-600">⏱</span>
                          <span>Display time: <span className="font-medium">{question.display_time_seconds}</span> seconds</span>
                        </div>
                      ) : (
                        <div className="text-gray-500">No minimum display time set</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* AI Follow-up Toggle - Hidden only for Conjoint Analysis */}
              {question.type !== 'conjoint_analysis' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      AI Follow-up Questions
                    </label>
                    {editingQuestionId === question.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`enable-ai-followup-${question.id}`}
                          checked={question.enable_ai_followup !== false}
                          onChange={(e) => updateQuestion(question.id, { enable_ai_followup: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`enable-ai-followup-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Enable AI follow-up questions
                        </label>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        question.enable_ai_followup
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {question.enable_ai_followup ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    {question.type === 'max_diff'
                      ? 'When enabled, AI will ask follow-up questions about the top 3 most appealing options after all MaxDiff sets are completed.'
                      : 'When enabled, AI may ask follow-up questions based on the respondent\'s answer to get deeper insights.'}
                  </p>
                </div>
              )}

              {/* Follow-up instructions - only show when AI follow-up is enabled */}
              {question.enable_ai_followup && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.FOLLOWUP_INSTRUCTIONS}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.FOLLOWUP_INSTRUCTIONS_HELP}
                  </p>
                  {editingQuestionId === question.id ? (
                    <textarea
                      value={question.followup_instructions || ''}
                      onChange={(e) => updateQuestion(question.id, { followup_instructions: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none text-sm"
                      rows={2}
                      placeholder={UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.LABELS.FOLLOWUP_INSTRUCTIONS_PLACEHOLDER}
                    />
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 min-h-[2.5rem]">
                      {question.followup_instructions ? (
                        <span className="text-sm text-gray-900">{question.followup_instructions}</span>
                      ) : (
                        <span className="text-sm text-gray-500 italic">No specific follow-up instructions</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Conditional Display Settings */}
              {index > 0 && (
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Display Condition
                    </label>
                    {editingQuestionId === question.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`enable-condition-${question.id}`}
                          checked={!!question.display_condition}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Find first eligible previous question
                              const eligibleQuestions = questions.slice(0, index).filter(
                                q => DISPLAY_CONDITION_TYPES.includes(q.type)
                              );
                              if (eligibleQuestions.length > 0) {
                                const sourceQ = eligibleQuestions[eligibleQuestions.length - 1];
                                // Initialize differently based on question type
                                if (sourceQ.type === 'percentage_allocation') {
                                  updateQuestion(question.id, {
                                    display_condition: {
                                      source_question_id: sourceQ.id,
                                      operator: '>',
                                      option_key: sourceQ.choices?.[0] || '',
                                      threshold: 0,
                                      value: ''
                                    }
                                  });
                                } else {
                                  updateQuestion(question.id, {
                                    display_condition: {
                                      source_question_id: sourceQ.id,
                                      operator: 'selected',
                                      value: sourceQ.choices?.[0] || ''
                                    }
                                  });
                                }
                              }
                            } else {
                              updateQuestion(question.id, { display_condition: undefined });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`enable-condition-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Add display condition
                        </label>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        question.display_condition
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {question.display_condition ? 'Conditional' : 'Disabled'}
                      </span>
                    )}
                  </div>

                  {/* Condition Configuration - only show when editing and condition is enabled */}
                  {editingQuestionId === question.id && question.display_condition && (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
                      <p className="text-xs text-gray-600">
                        Only show this question if:
                      </p>
                      <div className="grid grid-cols-1 gap-3">
                        {/* Source Question Selector */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">In question:</label>
                          <select
                            value={question.display_condition.source_question_id}
                            onChange={(e) => {
                              const sourceQ = questions.find(q => q.id === e.target.value);
                              // Reinitialize based on question type
                              if (sourceQ?.type === 'percentage_allocation') {
                                updateQuestion(question.id, {
                                  display_condition: {
                                    source_question_id: e.target.value,
                                    operator: '>',
                                    option_key: sourceQ.choices?.[0] || '',
                                    threshold: 0,
                                    value: ''
                                  }
                                });
                              } else {
                                updateQuestion(question.id, {
                                  display_condition: {
                                    source_question_id: e.target.value,
                                    operator: 'selected',
                                    value: sourceQ?.choices?.[0] || ''
                                  }
                                });
                              }
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            {questions.slice(0, index)
                              .filter(q => DISPLAY_CONDITION_TYPES.includes(q.type))
                              .map((q, idx) => (
                                <option key={q.id} value={q.id}>
                                  Q{questions.indexOf(q) + 1}: {q.text.substring(0, 40)}{q.text.length > 40 ? '...' : ''}
                                </option>
                              ))
                            }
                          </select>
                        </div>

                        {/* Conditional UI based on source question type */}
                        {(() => {
                          const sourceQ = questions.find(q => q.id === question.display_condition?.source_question_id);
                          const isPercentageAllocation = sourceQ?.type === 'percentage_allocation';

                          if (isPercentageAllocation) {
                            // Percentage Allocation UI
                            return (
                              <>
                                {/* Allocation Option Selector */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Allocation for:</label>
                                  <select
                                    value={question.display_condition?.option_key || ''}
                                    onChange={(e) => updateQuestion(question.id, {
                                      display_condition: {
                                        ...question.display_condition!,
                                        option_key: e.target.value
                                      }
                                    })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  >
                                    {(sourceQ?.choices || []).map((choice, idx) => (
                                      <option key={idx} value={choice}>{choice}</option>
                                    ))}
                                  </select>
                                </div>

                                {/* Numeric Operator and Threshold */}
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Is:</label>
                                    <select
                                      value={question.display_condition?.operator || '>'}
                                      onChange={(e) => updateQuestion(question.id, {
                                        display_condition: {
                                          ...question.display_condition!,
                                          operator: e.target.value as '>' | '>=' | '=' | '<' | '<='
                                        }
                                      })}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    >
                                      <option value=">">&gt; (greater than)</option>
                                      <option value=">=">&ge; (at least)</option>
                                      <option value="=">=  (equals)</option>
                                      <option value="<">&lt; (less than)</option>
                                      <option value="<=">&le; (at most)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Value (max {sourceQ?.allocation_target || 100}):</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max={sourceQ?.allocation_target || 100}
                                      value={question.display_condition?.threshold ?? 0}
                                      onChange={(e) => {
                                        const maxValue = sourceQ?.allocation_target || 100;
                                        const value = parseInt(e.target.value) || 0;
                                        const clampedValue = Math.min(maxValue, Math.max(0, value));
                                        updateQuestion(question.id, {
                                          display_condition: {
                                            ...question.display_condition!,
                                            threshold: clampedValue
                                          }
                                        });
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                </div>
                              </>
                            );
                          } else {
                            // Choice-based UI (existing)
                            return (
                              <>
                                {/* Operator Selector */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">User has:</label>
                                  <select
                                    value={question.display_condition?.operator || 'selected'}
                                    onChange={(e) => updateQuestion(question.id, {
                                      display_condition: {
                                        ...question.display_condition!,
                                        operator: e.target.value as 'selected' | 'not_selected'
                                      }
                                    })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  >
                                    <option value="selected">selected</option>
                                    <option value="not_selected">not selected</option>
                                  </select>
                                </div>

                                {/* Value Selector */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Option:</label>
                                  <select
                                    value={question.display_condition?.value || ''}
                                    onChange={(e) => updateQuestion(question.id, {
                                      display_condition: {
                                        ...question.display_condition!,
                                        value: e.target.value
                                      }
                                    })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  >
                                    {(sourceQ?.choices || []).map((choice, idx) => (
                                      <option key={idx} value={choice}>{choice}</option>
                                    ))}
                                  </select>
                                </div>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Show condition summary when not editing */}
                  {editingQuestionId !== question.id && question.display_condition && (
                    <div className="text-sm text-gray-600">
                      {(() => {
                        const sourceQ = questions.find(q => q.id === question.display_condition?.source_question_id);
                        const sourceIdx = sourceQ ? questions.indexOf(sourceQ) + 1 : '?';

                        if (sourceQ?.type === 'percentage_allocation') {
                          // Allocation-based summary
                          const operatorDisplay = {
                            '>': '>',
                            '>=': '≥',
                            '=': '=',
                            '<': '<',
                            '<=': '≤'
                          }[question.display_condition.operator] || question.display_condition.operator;
                          return `Show if Q${sourceIdx} "${question.display_condition.option_key}" ${operatorDisplay} ${question.display_condition.threshold}`;
                        } else {
                          // Choice-based summary
                          return `Show if Q${sourceIdx} ${question.display_condition.operator === 'selected' ? '=' : '≠'} "${question.display_condition.value}"`;
                        }
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
            </React.Fragment>
          ))}
        </div>

        {/* Add New Question Button - only visible when not in readonly mode and not editing */}
        {!readOnly && editingQuestionId === null && (
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={addNewQuestion}
              className="text-blue-600 hover:text-blue-700 border-blue-300"
            >
              + Add Question
            </Button>
          </div>
        )}
      </div>

      {/* Response Quota Configuration - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Response Quota
            </h3>
            <div className="text-sm font-medium text-gray-700 mb-1">
              Maximum Responses
            </div>
            <div className="text-sm text-gray-900">
              {maxResponses || 'Unlimited'}
            </div>
          </div>
        </div>
      )}

      {/* Response Quota Configuration - Edit Mode */}
      {!readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowQuotaConfig(!showQuotaConfig)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-900">
                Response Quota (Optional)
              </h3>
            </div>
            <span className="text-gray-400">
              {showQuotaConfig ? '−' : '+'}
            </span>
          </button>

          {showQuotaConfig && (
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label htmlFor="maxResponses" className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Responses
                </label>
                <input
                  type="number"
                  id="maxResponses"
                  value={maxResponses}
                  onChange={(e) => setMaxResponses(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g., 100"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Set a maximum number of responses for this survey. Leave empty for unlimited responses. When the quota is reached, new participants will be redirected (if redirect config is set).
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Panel Provider Redirect Configuration - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4 space-y-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Panel Provider Integration
            </h3>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Redirect Base URL
              </div>
              <div className="text-sm text-gray-900">
                {redirectBaseUrl || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Tracking Parameter Name
              </div>
              <div className="text-sm text-gray-900">
                {trackingParamName || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Completion Parameters
              </div>
              <div className="text-sm text-gray-900">
                {completionParams || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Screen Out Parameters
              </div>
              <div className="text-sm text-gray-900">
                {screenOutParams || 'Not configured'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">
                Quota Full Parameters
              </div>
              <div className="text-sm text-gray-900">
                {quotaFullParams || 'Not configured'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel Provider Redirect Configuration - Edit Mode */}
      {!readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowRedirectConfig(!showRedirectConfig)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-900">
                Panel Provider Integration (Optional)
              </h3>
            </div>
            <span className="text-gray-400">
              {showRedirectConfig ? '−' : '+'}
            </span>
          </button>

          {showRedirectConfig && (
            <div className="p-4 pt-0 space-y-4">
              <p className="text-xs text-gray-600 mb-4">
                Configure redirect URLs to send participants back to your panel provider after completing the survey.
              </p>

              {/* Provider Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Panel Provider
                </label>
                <select
                  value={selectedProvider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select Provider...</option>
                  <option value="userinterviews">User Interviews</option>
                  <option value="dynata">Dynata</option>
                  <option value="custom">Custom</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a provider to auto-fill configuration fields with recommended values
                </p>
              </div>

              {/* Redirect Base URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Redirect Base URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={redirectBaseUrl}
                  onChange={(e) => setRedirectBaseUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="https://example.com/survey/complete"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Base URL for redirecting participants (required if using panel provider integration)
                </p>
              </div>

              {/* Tracking Parameter Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tracking Parameter Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={trackingParamName}
                  onChange={(e) => setTrackingParamName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="iid (for UserInterviews) or psid (for Dynata)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Parameter name for participant tracking ID (e.g., "iid", "psid")
                </p>
              </div>

              {/* Completion Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Completion Parameters
                </label>
                <input
                  type="text"
                  value={completionParams}
                  onChange={(e) => setCompletionParams(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="rst=1, basic=YYYY"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Additional parameters for completion redirect (optional)
                </p>
              </div>

              {/* Screen Out Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Screen Out Parameters
                </label>
                <input
                  type="text"
                  value={screenOutParams}
                  onChange={(e) => setScreenOutParams(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="rst=2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Parameters for screen-out redirect (optional)
                </p>
              </div>

              {/* Quota Full Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quota Full Parameters
                </label>
                <input
                  type="text"
                  value={quotaFullParams}
                  onChange={(e) => setQuotaFullParams(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="rst=3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Parameters for quota-full redirect (optional)
                </p>
              </div>

              {/* Example Configuration */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <p className="text-xs font-medium text-blue-900 mb-2">Example Configuration:</p>
                <div className="text-xs text-blue-800 space-y-1">
                  <p><strong>UserInterviews:</strong></p>
                  <p>• Base URL: https://www.userinterviews.com/project-task-events/custom_link_with_redirect/complete</p>
                  <p>• Tracking Param: iid</p>
                  <p className="mt-2"><strong>Dynata:</strong></p>
                  <p>• Base URL: https://dkr1.ssisurveys.com/projects/end</p>
                  <p>• Tracking Param: psid</p>
                  <p>• Completion Params: rst=1, basic=YYYY</p>
                  <p>• Screen Out Params: rst=2</p>
                  <p>• Quota Full Params: rst=3</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Changes Button - only in edit mode for existing surveys */}
      {!readOnly && hideActionButtons && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={() => {
              // Build redirect config
              let redirectConfig: RedirectConfig | null = null;
              if (redirectBaseUrl.trim()) {
                const completionParamsObj = parseParams(completionParams);
                const screenOutParamsObj = parseParams(screenOutParams);
                const quotaFullParamsObj = parseParams(quotaFullParams);

                const trackingParam: { [key: string]: string } = {};
                if (trackingParamName.trim()) {
                  trackingParam[trackingParamName.trim()] = '{TRACKING_ID}';
                }

                redirectConfig = {
                  redirect_base_url: redirectBaseUrl.trim(),
                  tracking_param: trackingParam,
                  completion_params: Object.keys(completionParamsObj).length > 0 ? completionParamsObj : null,
                  screen_out_params: Object.keys(screenOutParamsObj).length > 0 ? screenOutParamsObj : null,
                  quota_full_params: Object.keys(quotaFullParamsObj).length > 0 ? quotaFullParamsObj : null,
                };
              }

              // Parse maxResponses
              const maxResponsesValue = maxResponses.trim() ? parseInt(maxResponses) : null;

              // Save all changes and exit edit mode
              onQuestionsUpdated(questions, redirectConfig, maxResponsesValue);
              onGoBack(); // Exit edit mode
            }}
            icon={<Save className="w-4 h-4" />}
            disabled={isLoading}
          >
            Save Changes
          </Button>
        </div>
      )}

      {/* Actions */}
      {!readOnly && !hideActionButtons && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.QUESTIONS_COUNT(questions.length)}
          </p>

          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onGoBack}
            >
              {UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.RE_ENTER_QUESTIONS}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateSurvey}
              disabled={editingQuestionId !== null || isSubmitting}
            >
              {isSubmitting ? UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CREATING_SURVEY : UI_TEXT.AI_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CREATE_SURVEY}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyQuestionsPreview;